import React, { useState, useEffect } from 'react';
import pb from '../lib/pocketbase';

export default function ProjectPersistenceModal({
  isOpen,
  onClose,
  currentProject,
  onLoad,
  onSaveSuccess,
  currentProjectId
}) {
  const [mode, setMode] = useState('list'); // 'list', 'save', 'login'
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Reset state when opening
  useEffect(() => {
    if (isOpen) {
      setError('');
      if (!pb.authStore.isValid) {
        setMode('login');
      } else {
        fetchProjects();
        setMode('list');
      }
    }
  }, [isOpen]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      await pb.collection('users').authWithPassword(email, password);
      setMode('list');
      fetchProjects();
    } catch (err) {
      setError('Login failed: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProjects = async () => {
    setIsLoading(true);
    try {
      const records = await pb.collection('projects').getFullList({
        sort: '-updated',
      });
      setProjects(records);
    } catch (err) {
      if (err.status !== 404) {
        setError('Failed to load projects: ' + err.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!saveName.trim()) {
      setError('Project name is required');
      return;
    }

    setIsLoading(true);
    setError('');

    const payload = {
      name: saveName,
      svg_content: currentProject.svgContent || '',
      config: currentProject.config || {},
      mappings: currentProject.mappings || {},
      data_source: currentProject.dataSource || {},
      user: pb.authStore.model?.id,
    };

    try {
      let record;
      if (currentProjectId) {
        // Update existing
        // We might want to ask confirmation if name changes, but for now just update
        // If the user wants to "Save As", they should clear the ID or we provide a separate flow.
        // For simplicity, this acts as "Save As" if no ID, or we forcing a new save?
        // Let's assume this is "Create New" or "Overwrite" based on if ID matches strictly?
        // Actually, let's keep it simple: List allows "Load". "Save" creates new or updates if we implement that logic.
        // If we want "Save" vs "Save As", we need more UI.
        // Let's implement "Save New" for now in the 'save' mode.
        record = await pb.collection('projects').create(payload);
      } else {
        record = await pb.collection('projects').create(payload);
      }

      onSaveSuccess(record.id);
      onClose();
    } catch (err) {
      setError('Save failed: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!currentProjectId) return;
    setIsLoading(true);
    try {
      const payload = {
        svg_content: currentProject.svgContent || '',
        config: currentProject.config || {},
        mappings: currentProject.mappings || {},
        data_source: currentProject.dataSource || {},
      };
      // Only update name if user explicitly changed it in a hypothetical settings, 
      // but here we are just quick-saving the state to the existing ID.
      await pb.collection('projects').update(currentProjectId, payload);
      onSaveSuccess(currentProjectId);
      onClose();
    } catch (err) {
      setError('Update failed: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  }

  const handleLoad = (record) => {
    onLoad({
      id: record.id,
      name: record.name,
      svgContent: record.svg_content,
      config: record.config,
      mappings: record.mappings, // Note: The schema put mappings in separate column, but legacy stored it in config sometimes? 
      // Based on our schema: mappings IS a column.
      // But in app state: config.mappings exists too. 
      // We should reconcile. Ideally app uses config.mappings. 
      // If we store it separately, we merge it back.
      // Let's assume we merge `mappings` column back into `config.mappings` if needed or pass it separately.
      dataSource: record.data_source
    });
    onClose();
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure?")) return;
    try {
      await pb.collection('projects').delete(id);
      fetchProjects();
    } catch (err) {
      setError("Delete failed");
    }
  }

  if (!isOpen) return null;

  return (
    <div className="code-modal-overlay">
      <div className="code-modal" style={{ maxWidth: '500px' }}>
        <div className="code-modal-header">
          <h3>
            {mode === 'login' && 'Login to PocketBase'}
            {mode === 'list' && 'Project Manager'}
            {mode === 'save' && 'Save Project'}
          </h3>
          <button className="btn btn--outline btn--sm" onClick={onClose}>✕</button>
        </div>

        <div className="code-modal-content" style={{ maxHeight: '60vh', overflowY: 'auto', padding: '16px' }}>
          {error && <div className="status status--error" style={{ marginBottom: '16px' }}>{error}</div>}

          {mode === 'login' && (
            <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <input
                className="form-control"
                placeholder="Email"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
              <input
                className="form-control"
                type="password"
                placeholder="Password"
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
              <button type="submit" disabled={isLoading} className="btn btn--primary">
                {isLoading ? 'Logging in...' : 'Login'}
              </button>
              <div style={{ marginTop: '10px', fontSize: '0.8em', color: '#666' }}>
                Ensure your PocketBase is running at <code>{pb.baseUrl}</code>
              </div>
            </form>
          )}

          {mode === 'list' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                <button
                  className="btn btn--secondary btn--sm"
                  onClick={() => setMode('save')}
                >
                  + New Save
                </button>
                {currentProjectId && (
                  <button
                    className="btn btn--primary btn--sm"
                    onClick={handleUpdate}
                  >
                    💾 Update Current
                  </button>
                )}
              </div>

              {isLoading ? (
                <p>Loading projects...</p>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {projects.length === 0 && <p>No projects found.</p>}
                  {projects.map(p => (
                    <li
                      key={p.id}
                      style={{
                        border: '1px solid var(--color-border)',
                        padding: '8px',
                        marginBottom: '8px',
                        borderRadius: '4px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                      onClick={() => handleLoad(p)}
                      className="project-item"
                    >
                      <div>
                        <strong>{p.name}</strong>
                        <div style={{ fontSize: '0.8em', color: '#888' }}>
                          Updated: {new Date(p.updated).toLocaleString()}
                        </div>
                      </div>
                      <button
                        className="btn btn--outline btn--sm danger"
                        onClick={(e) => handleDelete(p.id, e)}
                        style={{ marginLeft: '10px' }}
                      >
                        🗑
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {mode === 'save' && (
            <div>
              <label className="form-label">Project Name</label>
              <input
                className="form-control"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder="My Awesome Dashboard"
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
                <button className="btn btn--outline" onClick={() => setMode('list')}>Cancel</button>
                <button className="btn btn--primary" onClick={handleSave} disabled={isLoading}>
                  {isLoading ? 'Saving...' : 'Save As New'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
